/**
 * Editor for a scene variant's `media: MediaBlock[]` array (V5).
 *
 * Each block is one asset — image, video, or audio — addressed by URL. Blocks
 * render in order; the runtime plays them top-to-bottom. Authors can add,
 * remove, and reorder blocks, and edit the kind-appropriate accessibility
 * fields:
 *   - image  → alt text
 *   - video  → captions track URL + transcript
 *   - audio  → captions track URL + transcript
 *
 * Asset URL: in cloud mode, an "Upload" button (Phase 6 slice 2) uploads a
 * file to Supabase Storage and writes the returned public URL into the block.
 * In local mode (no Supabase configured) the picker is hidden and authors paste
 * a URL by hand. Either way the field is a plain URL — upload just fills it in.
 *
 * Contract: `media` may be undefined (treated as empty). `onChange(next)` is
 * called with the new array. An empty array is valid and means "no media."
 * `scenarioId` is used only to namespace uploaded files in Storage.
 */
import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Image as ImageIcon, Video, Music, Upload, Loader2, X } from 'lucide-react';
import { newId, type MediaBlock, type MediaKind } from '@branchworks/shared';
import { isCloudPersistence } from '../../persistence';
import { uploadMediaAsset, acceptForKind } from '../../persistence/storage';
import { useScenarioStore } from '../../store/scenarioStore';

interface MediaListEditorProps {
  media: MediaBlock[] | undefined;
  onChange: (next: MediaBlock[]) => void;
}

const KIND_META: Record<MediaKind, { label: string; Icon: typeof ImageIcon }> = {
  image: { label: 'Image', Icon: ImageIcon },
  video: { label: 'Video', Icon: Video },
  audio: { label: 'Audio', Icon: Music },
};

export function MediaListEditor({ media, onChange }: MediaListEditorProps) {
  const list = media || [];
  // Used only to namespace uploaded files in Storage; 'unsaved' if no id yet.
  const scenarioId = useScenarioStore((s) => s.scenarioId) || 'unsaved';

  const update = (idx: number, patch: Partial<MediaBlock>) => {
    onChange(list.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const remove = (idx: number) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  const move = (idx: number, direction: -1 | 1) => {
    const swap = idx + direction;
    if (swap < 0 || swap >= list.length) return;
    const next = list.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  const add = (kind: MediaKind) => {
    onChange([...list, { id: newId('m'), kind, url: '' }]);
  };

  // Switching kind preserves url; drops fields that no longer apply so we don't
  // carry an alt on a video or a transcript on an image through export.
  const changeKind = (idx: number, kind: MediaKind) => {
    const cur = list[idx];
    const base: MediaBlock = { id: cur.id, kind, url: cur.url };
    if (kind === 'image') {
      if (cur.alt) base.alt = cur.alt;
    } else {
      if (cur.captionsUrl) base.captionsUrl = cur.captionsUrl;
      if (cur.transcript) base.transcript = cur.transcript;
    }
    onChange(list.map((m, i) => (i === idx ? base : m)));
  };

  return (
    <div className="space-y-2">
      {list.map((m, i) => (
        <MediaBlockRow
          key={m.id || i}
          block={m}
          scenarioId={scenarioId}
          isFirst={i === 0}
          isLast={i === list.length - 1}
          onPatch={(patch) => update(i, patch)}
          onChangeKind={(kind) => changeKind(i, kind)}
          onMoveUp={() => move(i, -1)}
          onMoveDown={() => move(i, 1)}
          onRemove={() => remove(i)}
        />
      ))}

      {/* Add controls — one per kind */}
      <div className="flex items-center gap-3">
        {(Object.keys(KIND_META) as MediaKind[]).map((k) => {
          const { Icon, label } = KIND_META[k];
          return (
            <button
              key={k}
              onClick={() => add(k)}
              className="text-xs flex items-center gap-1 hover:underline text-ink"
              title={`Add ${label.toLowerCase()}`}
            >
              <Icon size={11} /> Add {label.toLowerCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface MediaBlockRowProps {
  block: MediaBlock;
  scenarioId: string;
  isFirst: boolean;
  isLast: boolean;
  onPatch: (patch: Partial<MediaBlock>) => void;
  onChangeKind: (kind: MediaKind) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function MediaBlockRow({
  block: m,
  scenarioId,
  isFirst,
  isLast,
  onPatch,
  onChangeKind,
  onMoveUp,
  onMoveDown,
  onRemove,
}: MediaBlockRowProps) {
  const { Icon } = KIND_META[m.kind];
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Upload only makes sense in cloud mode (needs Storage + an auth'd user).
  const canUpload = isCloudPersistence();

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again re-triggers change.
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const { url } = await uploadMediaAsset(file, scenarioId, m.id);
      onPatch({ url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded border p-2 space-y-1.5 border-cream-border bg-white">
      {/* Header: kind selector + reorder/remove */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={12} className="text-ink-2 flex-shrink-0" />
          <select
            value={m.kind}
            onChange={(e) => onChangeKind(e.target.value as MediaKind)}
            className="px-1.5 py-1 text-xs bg-white rounded border border-cream-border"
            aria-label="Media type"
          >
            {(Object.keys(KIND_META) as MediaKind[]).map((k) => (
              <option key={k} value={k}>{KIND_META[k].label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up (shown earlier)"
            aria-label="Move media up"
          >
            <ChevronUp size={11} />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
            aria-label="Move media down"
          >
            <ChevronDown size={11} />
          </button>
          <button
            onClick={onRemove}
            className="p-0.5 rounded hover:bg-cream text-terracotta-dark"
            title="Remove media"
            aria-label="Remove media"
          >
            <X size={11} />
          </button>
        </div>
      </div>

      {/* URL + (cloud-mode) upload */}
      <div className="flex items-center gap-1.5">
        <input
          value={m.url}
          onChange={(e) => onPatch({ url: e.target.value })}
          placeholder={`https://… (${m.kind} URL)`}
          className="flex-1 min-w-0 px-2 py-1 rounded border border-cream-border bg-white text-xs"
        />
        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptForKind(m.kind)}
              onChange={onFilePicked}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1 px-2 py-1 rounded border border-cream-border text-xs hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title={`Upload ${m.kind} file`}
              aria-label={`Upload ${m.kind} file`}
            >
              {uploading ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Upload size={11} />
              )}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </>
        )}
      </div>
      {uploadError && (
        <div className="text-[11px] text-terracotta-dark">{uploadError}</div>
      )}

      {/* Accessibility fields, by kind */}
      {m.kind === 'image' ? (
        <input
          value={m.alt || ''}
          onChange={(e) => onPatch({ alt: e.target.value })}
          placeholder="Alt text (described for screen readers)"
          className="w-full px-2 py-1 rounded border border-cream-border bg-white text-xs"
        />
      ) : (
        <>
          <input
            value={m.captionsUrl || ''}
            onChange={(e) => onPatch({ captionsUrl: e.target.value })}
            placeholder="Captions track URL (WebVTT .vtt)"
            className="w-full px-2 py-1 rounded border border-cream-border bg-white text-xs"
          />
          <textarea
            value={m.transcript || ''}
            onChange={(e) => onPatch({ transcript: e.target.value })}
            rows={2}
            placeholder="Transcript (full text of the audio/video)"
            className="w-full px-2 py-1 rounded border border-cream-border bg-white text-xs leading-relaxed"
          />
        </>
      )}
    </div>
  );
}
